<template>
  <div ref="rootRef" class="relative">
    <button
      type="button"
      @click="toggleMenu"
      :aria-label="t('language.select')"
      :title="collapsed ? currentOption.name : undefined"
      aria-haspopup="listbox"
      :aria-expanded="isMenuOpen"
      :class="[
        'w-full flex items-center gap-3 px-3 rounded-lg text-sm font-medium transition-colors',
        compact ? 'py-2.5' : 'py-3',
        collapsed && 'justify-center',
        isMenuOpen
          ? 'bg-casino-primary/20 text-casino-primary-light'
          : 'text-gray-400 hover:text-white hover:bg-white/5'
      ]"
    >
      <Globe class="w-5 h-5 flex-shrink-0" />
      <template v-if="!collapsed">
        <span class="flex-1 truncate text-left">{{ currentOption.name }}</span>
        <span class="text-base leading-none" aria-hidden="true">{{ currentOption.flag }}</span>
        <ChevronUp
          class="w-4 h-4 flex-shrink-0 transition-transform duration-200"
          :class="isMenuOpen ? 'rotate-0' : 'rotate-180'"
        />
      </template>
    </button>

    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <ul
        v-if="isMenuOpen"
        role="listbox"
        :aria-label="t('language.select')"
        :class="[
          'absolute z-[9700] p-1.5 bg-casino-card border border-white/10 rounded-xl shadow-2xl shadow-black/50 space-y-0.5',
          collapsed ? 'left-full bottom-0 ml-3 w-48' : 'left-0 right-0 bottom-full mb-2'
        ]"
      >
        <li
          v-if="collapsed"
          class="px-3 pt-1.5 pb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider"
        >
          {{ t('language.title') }}
        </li>
        <li v-for="option in LOCALES" :key="option.code">
          <button
            type="button"
            role="option"
            :aria-selected="option.code === locale"
            @click="selectLocale(option.code)"
            :class="[
              'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
              option.code === locale
                ? 'bg-casino-primary/20 text-casino-primary-light'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            ]"
          >
            <span class="text-base leading-none" aria-hidden="true">{{ option.flag }}</span>
            <span class="flex-1 text-left truncate">{{ option.name }}</span>
            <Check v-if="option.code === locale" class="w-4 h-4 flex-shrink-0" />
          </button>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, ChevronUp, Globe } from 'lucide-vue-next'
import { LOCALES, setLocale } from '@/i18n'

defineProps({
  /** Sidebar de escritorio colapsado: solo el icono, el menú se abre hacia la derecha. */
  collapsed: {
    type: Boolean,
    default: false
  },
  /** Mismo alto de fila que los enlaces del sidebar de escritorio (py-2.5) en vez del drawer móvil (py-3). */
  compact: {
    type: Boolean,
    default: false
  }
})

const { t, locale } = useI18n()

const isMenuOpen = ref(false)
const rootRef = ref(null)

const currentOption = computed(
  () => LOCALES.find((option) => option.code === locale.value) ?? LOCALES[0]
)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const selectLocale = (code) => {
  isMenuOpen.value = false
  if (code === locale.value) return
  setLocale(code)
}

const onDocumentClick = (event) => {
  if (!rootRef.value || !(event.target instanceof Node)) return
  if (!rootRef.value.contains(event.target)) isMenuOpen.value = false
}

const onDocumentKeydown = (event) => {
  if (event.key === 'Escape') isMenuOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onDocumentKeydown)
})
</script>
